import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import {HubspotForm} from '@/components/hubspot-form'
import {Text} from '@/components/text'
import {useGlobalStrings} from '@/providers/global-strings'
import {BackgroundWrapper, Layout, Section} from '@/styles/grid'
import {tokens} from '@/theme'
import getEnvironmentField from '@/utils/get-environment-field'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import mq from '@/utils/media-query'
import replacePlaceholders from '@/utils/replace-placeholders'

import type {InvoiceTemplateFormFragment} from '@/graphql-types'
import type {FormConfig} from '@/types/consents'

interface Props {
    content: InvoiceTemplateFormFragment
}

export const InvoiceTemplateForm = ({content}: Props) => {
    const data = content.data

    const globalStrings = useGlobalStrings()
    const [formSubmitted, setFormubmitted] = React.useState(false)
    const [leadName, setLeadName] = React.useState('')

    if (!data) {
        return handleNoPrismicData()
    }

    const formConfig = getEnvironmentField({
        production: data.form_production,
        staging: data.form_staging,
    }) as FormConfig

    const personalisedSuccessMessage = replacePlaceholders(data.successMessage, {
        '{{name}}': leadName,
    })

    return (
        <BackgroundWrapper $color="grey">
            <StyledSection>
                <Layout $variant="two-asymmetrical">
                    <Content>
                        <Text forwardedAs="h2" $variant="heading-base">
                            {formSubmitted ? personalisedSuccessMessage : data.title}
                        </Text>
                        {!formSubmitted && (
                            <Text $variant="paragraph-base" style={{marginTop: tokens.spacing20}}>
                                {data.description}
                            </Text>
                        )}
                    </Content>

                    {!formSubmitted && (
                        <HubspotForm
                            formConfig={formConfig}
                            shouldRedirect={false}
                            onSubmitSuccess={(state) => {
                                setFormubmitted(true)
                                setLeadName(state.firstName)
                            }}
                            content={{submitButtonText: globalStrings.submit}}
                            hiddenFields={[
                                'country',
                                'companySize',
                                'discoveredVia',
                                'phoneNumber',
                                'phoneNumber',
                            ]}
                        />
                    )}
                </Layout>
            </StyledSection>
        </BackgroundWrapper>
    )
}

const MOBILE_BP = 'xs'

const StyledSection = styled(Section)`
    ${mq.max(MOBILE_BP)} {
        padding-left: 0;
        padding-right: 0;
    }
`

const Content = styled.div`
    ${mq.max(MOBILE_BP)} {
        padding-left: ${tokens.spacing24};
        padding-right: ${tokens.spacing24};
    }
`

export const invoiceTemplateFormFragment = graphql`
    fragment InvoiceTemplateForm on PrismicInvoiceTemplateForm {
        __typename
        data {
            title
            description
            successMessage: success_message
            form_production {
                formId
                consents {
                    type
                    communicationConsents {
                        id
                        required
                    }
                }
            }
            form_staging {
                formId
                consents {
                    type
                    communicationConsents {
                        id
                        required
                    }
                }
            }
        }
    }
`
